import "./App.css";
import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { WalletProvider } from "./contexts/WalletContext";
import { AuthProvider } from "./contexts/AuthContext";
import AppRouter from "./components/AppRouter";
import ProtectedRoute from "./components/ProtectedRoute";
import Onboarding from "./pages/Onboarding";
import PalmRegister from "./pages/PalmRegister";
import Auth from "./pages/Auth";
import VerifyOTP from "./pages/VerifyOTP";
import ForgotPassword from "./pages/ForgotPassword";
import PalmEnrollment from "./pages/PalmEnrollment";
import PremiumHome from "./pages/PremiumHome";
import PremiumServices from "./pages/PremiumServices";
import Palm2QR from "./pages/Palm2QR";
import POSMode from "./pages/POSMode";
import PalmTransfer from "./pages/PalmTransfer";
import PalmHistory from "./pages/PalmHistory";
import SpendsPage from "./pages/SpendsPage";
import PalmProfile from "./pages/PalmProfile";
import EditProfile from "./pages/EditProfile";
import DeviceLocator from "./pages/DeviceLocator";
import DeviceEnrollment from "./pages/DeviceEnrollment";
import ReceiptViewer from "./pages/ReceiptViewer";
import QuickWallet from "./pages/QuickWallet";
import SecurityCenter from "./pages/SecurityCenter";
import Rewards from "./pages/Rewards";
import Support from "./pages/Support";
import DeviceCenter from "./pages/DeviceCenter";
import LimitSettings from "./pages/LimitSettings";
import DigitalWallet from "./pages/DigitalWallet";
import CollectRequest from "./pages/CollectRequest";
import CirclePay from "./pages/CirclePay";
import ReferralProgram from "./pages/ReferralProgram";
import DisputeCenter from "./pages/DisputeCenter";
import SendMoney from "./pages/SendMoney";
import LanguageSelector from "./pages/LanguageSelector";
import OfflineQueue from "./pages/OfflineQueue";
import AutoPay from "./pages/AutoPay";
import CashbackRewards from "./pages/CashbackRewards";
import AllServices from "./pages/AllServices";
import WalletManagement from "./pages/WalletManagement";
import AnimatedHome from "./pages/AnimatedHome";
import AnimatedHomeWithLayouts from "./pages/AnimatedHomeWithLayouts";
import AnimatedSendMoney from "./pages/AnimatedSendMoney";
import PremiumComponentsDemo from "./pages/PremiumComponentsDemo";
import AllComponentsDemo from "./pages/AllComponentsDemo";
import PatentBadgesOnboarding from "./pages/PatentBadgesOnboarding";
import WalletPage from "./pages/WalletPage";
import PalmScan from "./pages/PalmScan";
import AdminDashboard from "./admin/pages/AdminDashboard";
import UserManagement from "./admin/pages/UserManagement";
import DeviceManagement from "./admin/pages/DeviceManagement";
import AdminSendMoney from "./admin/pages/SendMoney";
import Transactions from "./admin/pages/Transactions";
import { Toaster } from "./components/ui/toaster";
import { Home as HomeIcon, Grid3x3, ArrowLeftRight, Clock, User } from "lucide-react";

// Service pages - Top 8 Priority
import MobileRecharge from "./pages/services/MobileRecharge";
import ElectricityBill from "./pages/services/ElectricityBill";
import DTHRecharge from "./pages/services/DTHRecharge";
import BroadbandBill from "./pages/services/BroadbandBill";
import GasBill from "./pages/services/GasBill";
import WaterBill from "./pages/services/WaterBill";
import FASTagRecharge from "./pages/services/FASTagRecharge";
import Insurance from "./pages/services/Insurance";

// Service pages - Travel
import FlightBooking from "./pages/services/FlightBooking";
import HotelBooking from "./pages/services/HotelBooking";
import ComingSoon from "./pages/services/ComingSoon";

// Service pages - Additional
import DataCard from "./pages/services/DataCard";
import LandlineBill from "./pages/services/LandlineBill";
import HousingSociety from "./pages/services/HousingSociety";
import CableTVBill from "./pages/services/CableTVBill";
import MutualFunds from "./pages/services/MutualFunds";
import DigitalGold from "./pages/services/DigitalGold";
import MetroTransit from "./pages/use-cases/MetroTransit";
import Schools from "./pages/use-cases/Schools";
import Offices from "./pages/use-cases/Offices";
import PalmCircle from "./pages/use-cases/PalmCircle";
import Hospitals from "./pages/use-cases/Hospitals";
import Retail from "./pages/use-cases/Retail";
import SmartCity from "./pages/use-cases/SmartCity";

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Hide navigation on certain pages
  const hideNavPages = ['/', '/onboarding', '/palm-register', '/palm2qr', '/pos-mode', '/device-locator', '/device-enrollment'];
  
  // Hide on admin pages and other special pages
  if (hideNavPages.includes(location.pathname) || 
      location.pathname.startsWith('/receipt/') ||
      location.pathname.startsWith('/admin')) {
    return null;
  }

  const navItems = [
    { id: 'home', label: 'Home', icon: HomeIcon, path: '/home' },
    { id: 'services', label: 'Services', icon: Grid3x3, path: '/services' },
    { id: 'transfer', label: 'Transfer', icon: ArrowLeftRight, path: '/transfer' },
    { id: 'history', label: 'History', icon: Clock, path: '/history' },
    { id: 'profile', label: 'Profile', icon: User, path: '/profile' },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
      <div className="max-w-md mx-auto flex items-center justify-around py-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                isActive ? 'text-[#586BFF]' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <item.icon className={`w-6 h-6 ${isActive ? 'stroke-[2.5]' : ''}`} />
              <span className={`text-xs ${isActive ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Conditional wrapper for mobile-first pages vs full-width pages (like admin)
const ContentWrapper = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  
  if (isAdminPage) {
    // Full width for admin pages
    return <div className="min-h-screen bg-gray-50">{children}</div>;
  }
  
  // Mobile-first width for regular pages
  return <div className="max-w-md mx-auto bg-white min-h-screen relative">{children}</div>;
};

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AuthProvider>
          <WalletProvider>
            <div className="App">
            <ContentWrapper>
            <AppRouter>
            <Routes>
              {/* Root path - handles initial routing via AppRouter */}
              <Route path="/" element={<div />} />
              
              {/* Public Routes - No Authentication Required */}
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/verify-otp" element={<VerifyOTP />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              
              {/* Protected Routes - Require Authentication */}
              <Route path="/palm-register" element={
                <ProtectedRoute>
                  <PalmRegister />
                </ProtectedRoute>
              } />
              <Route path="/palm-enrollment" element={
                <ProtectedRoute>
                  <PalmEnrollment />
                </ProtectedRoute>
              } />
              <Route path="/palm-scan" element={
                <ProtectedRoute>
                  <PalmScan />
                </ProtectedRoute>
              } />
              <Route path="/home" element={
                <ProtectedRoute>
                  <AnimatedHome />
                </ProtectedRoute>
              } />
            <Route path="/home-old" element={
              <ProtectedRoute>
                <PremiumHome />
              </ProtectedRoute>
            } />
            <Route path="/services" element={
              <ProtectedRoute>
                <PremiumServices />
              </ProtectedRoute>
            } />
            <Route path="/palm2qr" element={
              <ProtectedRoute>
                <Palm2QR />
              </ProtectedRoute>
            } />
            <Route path="/pos-mode" element={
              <ProtectedRoute>
                <POSMode />
              </ProtectedRoute>
            } />
            <Route path="/transfer" element={
              <ProtectedRoute>
                <PalmTransfer />
              </ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute>
                <PalmHistory />
              </ProtectedRoute>
            } />
            <Route path="/spends" element={
              <ProtectedRoute>
                <SpendsPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <PalmProfile />
              </ProtectedRoute>
            } />
            <Route path="/edit-profile" element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            } />
            <Route path="/device-locator" element={<DeviceLocator />} />
            <Route path="/device-enrollment" element={<DeviceEnrollment />} />
            <Route path="/receipt/:receiptId" element={<ReceiptViewer />} />
            <Route path="/quick-wallet" element={<QuickWallet />} />
            <Route path="/security-center" element={<SecurityCenter />} />
            <Route path="/rewards" element={<Rewards />} />
            <Route path="/support" element={<Support />} />
            <Route path="/device-center" element={<DeviceCenter />} />
            <Route path="/limit-settings" element={<LimitSettings />} />
            <Route path="/wallet" element={<DigitalWallet />} />
            <Route path="/collect" element={<CollectRequest />} />
            <Route path="/circlepay" element={<CirclePay />} />
            <Route path="/referral" element={<ReferralProgram />} />
            <Route path="/dispute" element={<DisputeCenter />} />
            <Route path="/send" element={<AnimatedSendMoney />} />
            <Route path="/send-old" element={<SendMoney />} />
            <Route path="/transfer" element={<SendMoney />} />
            <Route path="/language" element={<LanguageSelector />} />
            <Route path="/offline-queue" element={<OfflineQueue />} />
            <Route path="/autopay" element={<AutoPay />} />
            <Route path="/cashback" element={<CashbackRewards />} />
            <Route path="/all-services" element={<AllServices />} />
            
            {/* Service Routes - Top 8 Priority */}
            <Route path="/service/mobile" element={<MobileRecharge />} />
            <Route path="/services/mobile-recharge" element={<MobileRecharge />} />
            <Route path="/service/electricity" element={<ElectricityBill />} />
            <Route path="/services/electricity" element={<ElectricityBill />} />
            <Route path="/service/dth" element={<DTHRecharge />} />
            <Route path="/services/dth" element={<DTHRecharge />} />
            <Route path="/service/broadband" element={<BroadbandBill />} />
            <Route path="/services/broadband" element={<BroadbandBill />} />
            <Route path="/service/gas" element={<GasBill />} />
            <Route path="/services/gas" element={<GasBill />} />
            <Route path="/service/piped-gas" element={<GasBill />} />
            <Route path="/service/water" element={<WaterBill />} />
            <Route path="/services/water" element={<WaterBill />} />
            <Route path="/service/fastag" element={<FASTagRecharge />} />
            <Route path="/services/fastag" element={<FASTagRecharge />} />
            <Route path="/service/insurance" element={<Insurance />} />
            <Route path="/services/insurance" element={<Insurance />} />
            
            {/* Service Routes - Travel */}
            <Route path="/service/flights" element={<FlightBooking />} />
            <Route path="/services/flights" element={<FlightBooking />} />
            <Route path="/service/hotels" element={<HotelBooking />} />
            <Route path="/services/hotels" element={<HotelBooking />} />
            <Route path="/service/trains" element={<ComingSoon />} />
            <Route path="/service/bus" element={<ComingSoon />} />
            <Route path="/service/cab" element={<ComingSoon />} />
            
            {/* Service Routes - Additional Services */}
            <Route path="/service/datacard" element={<DataCard />} />
            <Route path="/services/datacard" element={<DataCard />} />
            <Route path="/service/landline" element={<LandlineBill />} />
            <Route path="/services/landline" element={<LandlineBill />} />
            <Route path="/service/housing" element={<HousingSociety />} />
            <Route path="/services/housing" element={<HousingSociety />} />
            <Route path="/service/cable" element={<CableTVBill />} />
            <Route path="/services/cable-tv" element={<CableTVBill />} />
            
            {/* Investment Services */}
            <Route path="/service/mutual" element={
              <ProtectedRoute>
                <MutualFunds />
              </ProtectedRoute>
            } />
            <Route path="/services/mutual-funds" element={
              <ProtectedRoute>
                <MutualFunds />
              </ProtectedRoute>
            } />
            <Route path="/service/gold" element={
              <ProtectedRoute>
                <DigitalGold />
              </ProtectedRoute>
            } />
            <Route path="/services/digital-gold" element={
              <ProtectedRoute>
                <DigitalGold />
              </ProtectedRoute>
            } />
            
            {/* Use-Case Hub Routes */}
            <Route path="/use-cases/metro" element={
              <ProtectedRoute>
                <MetroTransit />
              </ProtectedRoute>
            } />
            <Route path="/use-cases/schools" element={
              <ProtectedRoute>
                <Schools />
              </ProtectedRoute>
            } />
            <Route path="/use-cases/offices" element={
              <ProtectedRoute>
                <Offices />
              </ProtectedRoute>
            } />
            <Route path="/use-cases/palm-circle" element={
              <ProtectedRoute>
                <PalmCircle />
              </ProtectedRoute>
            } />
            <Route path="/use-cases/hospitals" element={
              <ProtectedRoute>
                <Hospitals />
              </ProtectedRoute>
            } />
            <Route path="/use-cases/retail" element={
              <ProtectedRoute>
                <Retail />
              </ProtectedRoute>
            } />
            <Route path="/use-cases/smart-city" element={
              <ProtectedRoute>
                <SmartCity />
              </ProtectedRoute>
            } />
            
            {/* Service Routes - Coming Soon */}
            <Route path="/service/loan" element={<ComingSoon />} />
            <Route path="/services/loans" element={<ComingSoon />} />
            <Route path="/services/loan-repayment" element={<ComingSoon />} />
            <Route path="/service/education" element={<ComingSoon />} />
            <Route path="/services/education" element={<ComingSoon />} />
            <Route path="/service/health" element={<ComingSoon />} />
            <Route path="/services/postpaid" element={<ComingSoon />} />
            <Route path="/services/municipal" element={<ComingSoon />} />
            <Route path="/services/rental" element={<ComingSoon />} />
            <Route path="/services/clubs" element={<ComingSoon />} />
            <Route path="/services/metro" element={<ComingSoon />} />
            <Route path="/services/lic" element={<ComingSoon />} />
            <Route path="/services/donation" element={<ComingSoon />} />
            <Route path="/services/echallan" element={<ComingSoon />} />
            <Route path="/services/cylinder" element={<ComingSoon />} />
            <Route path="/digital-wallet" element={<DigitalWallet />} />
            <Route path="/wallet" element={<WalletManagement />} />
            <Route path="/quickwallet" element={<QuickWallet />} />
            <Route path="/scan" element={<Palm2QR />} />
            <Route path="/cashback-rewards" element={<CashbackRewards />} />
            <Route path="/add-money" element={<QuickWallet />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/devices" element={<DeviceManagement />} />
            <Route path="/admin/transactions" element={<Transactions />} />
            <Route path="/admin/send-money" element={<AdminSendMoney />} />
            
            {/* Premium Components Demo */}
            <Route path="/premium-demo" element={<PremiumComponentsDemo />} />
            
            {/* Phase 2 Routes */}
            <Route path="/patent-badges" element={<PatentBadgesOnboarding />} />
            <Route path="/wallet-management" element={<WalletPage />} />
            
            {/* Phase 3 Routes */}
            <Route path="/home-layouts" element={<AnimatedHomeWithLayouts />} />
            
            {/* All Components Demo */}
            <Route path="/all-components" element={<AllComponentsDemo />} />
            </Routes>
            </AppRouter>
            <BottomNavigation />
            </ContentWrapper>
            <Toaster />
          </div>
          </WalletProvider>
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;